import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";

const FADE_UP = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const FADE_DELAY = { delay: 0.1 };
import { ChevronLeft, MapPin, Clock, Shield, Package, Truck, FileText, ArrowRight, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import { productImage } from "../lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(res => {
      setProduct(res.data);
    }).catch(() => { /* product not found */ }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen pt-24 flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-ib-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!product) return (
    <div className="min-h-screen pt-24 text-center px-6">
      <h2 className="font-outfit font-bold text-2xl text-ib-text mb-4">Product Not Found</h2>
      <Link to="/marketplace"><Button className="bg-ib-blue text-white font-outfit">Back to Marketplace</Button></Link>
    </div>
  );

  return (
    <div className="min-h-screen pt-20 pb-16 px-6" data-testid="product-detail-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/marketplace" className="inline-flex items-center gap-1 font-outfit text-sm text-ib-text-secondary hover:text-ib-blue transition-colors mb-6" data-testid="product-back-link">
          <ChevronLeft className="w-3 h-3" /> Back to Marketplace
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main info */}
          <motion.div {...FADE_UP} className="lg:col-span-2 space-y-6">
            {/* Header card */}
            <div className="glass rounded-xl overflow-hidden" data-testid="product-header">
              <div className="h-56 relative">
                <img src={productImage(product.category)} alt={product.category} className="w-full h-full object-cover" data-testid="product-detail-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-ib-surface via-ib-surface/30 to-transparent" />
              </div>
              <div className="p-6 -mt-10 relative">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <Badge variant="outline" className="border-ib-steel/30 text-ib-lime font-jetbrains text-[10px] mb-2">
                    {product.metal_type}
                  </Badge>
                  <h1 className="font-outfit font-extrabold text-2xl sm:text-3xl text-ib-text tracking-tight" data-testid="product-name">
                    {product.name}
                  </h1>
                  <p className="font-jetbrains text-xs text-ib-text-muted mt-1">by {product.supplier}</p>
                </div>
                <div className="text-right">
                  <div className="font-jetbrains text-2xl font-bold text-ib-lime" data-testid="product-price">
                    ₹{Number(product.price).toLocaleString("en-IN")}
                  </div>
                  <span className="font-jetbrains text-xs text-ib-text-muted">{product.currency}</span>
                </div>
              </div>
              <p className="font-manrope text-sm text-ib-text-secondary leading-relaxed">{product.description}</p>
              </div>
            </div>

            {/* Specifications */}
            <div className="glass rounded-xl p-6" data-testid="product-specs">
              <h3 className="font-outfit font-bold text-sm text-ib-text mb-4">Specifications</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { label: "Category", value: product.category, key: "cat" },
                  { label: "Grade", value: product.grade, key: "grd" },
                  { label: "Thickness", value: product.thickness, key: "thk" },
                  { label: "Width", value: product.width, key: "wdt" },
                  { label: "Min Order", value: `${product.min_order} tons`, key: "min" },
                  { label: "Available", value: `${Number(product.quantity_available).toLocaleString()} tons`, key: "avl" },
                ].map((spec) => (
                  <div key={spec.key} className="p-3 rounded-lg bg-ib-bg/50 border border-ib-steel/10">
                    <div className="font-jetbrains text-[10px] text-ib-text-muted uppercase tracking-wider mb-1">{spec.label}</div>
                    <div className="font-outfit font-semibold text-sm text-ib-text">{spec.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div className="glass rounded-xl p-6" data-testid="product-certifications">
              <h3 className="font-outfit font-bold text-sm text-ib-text mb-4">Certifications & Standards</h3>
              <div className="flex flex-wrap gap-2">
                {product.certifications?.map((cert) => (
                  <span key={cert} className="px-3 py-1.5 rounded-md text-xs font-jetbrains bg-ib-blue/10 text-ib-blue border border-ib-blue/20">
                    <Shield className="w-3 h-3 inline mr-1.5" />{cert}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div {...FADE_UP} transition={FADE_DELAY} className="space-y-4">
            {/* Quick info */}
            <div className="glass rounded-xl p-5 space-y-4" data-testid="product-sidebar">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-ib-bg/50 border border-ib-steel/10">
                <MapPin className="w-4 h-4 text-ib-blue flex-shrink-0" />
                <div>
                  <div className="font-jetbrains text-[10px] text-ib-text-muted">Supplier Location</div>
                  <div className="font-outfit text-sm text-ib-text">{product.location}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-ib-bg/50 border border-ib-steel/10">
                <Truck className="w-4 h-4 text-ib-blue flex-shrink-0" />
                <div>
                  <div className="font-jetbrains text-[10px] text-ib-text-muted">Delivery</div>
                  <div className="font-outfit text-sm text-ib-text">{product.delivery_days} days</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-ib-bg/50 border border-ib-steel/10">
                <Package className="w-4 h-4 text-ib-blue flex-shrink-0" />
                <div>
                  <div className="font-jetbrains text-[10px] text-ib-text-muted">Min Order Qty</div>
                  <div className="font-outfit text-sm text-ib-text">{product.min_order} tons</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-ib-bg/50 border border-ib-steel/10">
                <Clock className="w-4 h-4 text-ib-blue flex-shrink-0" />
                <div>
                  <div className="font-jetbrains text-[10px] text-ib-text-muted">Stock Status</div>
                  <div className="font-outfit text-sm text-ib-lime">In Stock</div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="glass rounded-xl p-5 space-y-3">
              <a href="mailto:sales@ironbyte.in?subject=Quote%20Request%20-%20Product%20Enquiry" className="block">
                <Button data-testid="product-request-quote-btn" className="w-full bg-ib-blue hover:bg-ib-blue/90 text-white font-outfit gap-2 py-5 rounded-full">
                  <FileText className="w-4 h-4" /> Request Quote <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
              <p className="font-manrope text-[10px] text-ib-text-muted text-center">Our team responds within 2 hours during business hours</p>
              <div className="flex flex-col gap-1.5 pt-2 border-t border-ib-steel/10">
                <a href="mailto:sales@ironbyte.in" data-testid="product-email-sales" className="flex items-center gap-2 font-jetbrains text-[10px] text-ib-text-muted hover:text-ib-blue transition-colors">
                  <Mail className="w-3 h-3" /> sales@ironbyte.in
                </a>
                <a href="mailto:ironbytetech@gmail.com" data-testid="product-email-general" className="flex items-center gap-2 font-jetbrains text-[10px] text-ib-text-muted hover:text-ib-blue transition-colors">
                  <Mail className="w-3 h-3" /> ironbytetech@gmail.com
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
